import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react';

import type { FeatureFlag, FeatureFlagState } from './use-feature-flag-utils';
import { useFeatureFlagUtils } from './use-feature-flag-utils';

interface FeatureFlagContextType<T extends string = string> {
  flags: FeatureFlagState;
  isFeatureEnabled: (flagName: T) => boolean;
  toggleFeature: (flagName: T) => void;
  isDialogOpen: boolean;
  openDialog: () => void;
  closeDialog: () => void;
  allFeatureFlags: FeatureFlag[];
}

interface FeatureFlagProviderProps {
  children: ReactNode;
  featureFlags: FeatureFlag[];
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({ children, featureFlags }) => {
  const { flags, isFeatureEnabled, toggleFeature } = useFeatureFlagUtils(featureFlags);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const openDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
    location.reload();
  }, []);

  const contextValue: FeatureFlagContextType = {
    flags,
    isFeatureEnabled,
    toggleFeature,
    isDialogOpen,
    openDialog,
    closeDialog,
    allFeatureFlags: featureFlags,
  };

  return <FeatureFlagContext.Provider value={contextValue}>{children}</FeatureFlagContext.Provider>;
};

export const useFeatureFlags = <T extends string>(): FeatureFlagContextType<T> => {
  const context = useContext(FeatureFlagContext);

  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }

  return context;
};

export const useFeatureFlag = (flagName: string): boolean => {
  const { isFeatureEnabled } = useFeatureFlags();
  return isFeatureEnabled(flagName);
};
